import type { GroupUser } from '~/types/groups'

// Order of the @mention autocomplete:
//   1. the member I mentioned last in this group,
//   2. up to `RECENT_ACTIVE_COUNT` members most recently active in its chats (newest first),
//   3. «@everyone» / «@here» and the roles I may mention (`broadcast`),
//   4. everyone else — the server's search results for a query (it searches the whole group, which
//      can be far larger than the pages loaded here), then loaded members it didn't return.
// With a query every tier keeps only the members that match it; the tiers keep their order.

export const RECENT_ACTIVE_COUNT = 5
export const MENTION_SUGGESTIONS_LIMIT = 30

/** Lowercased, «ё» folded into «е» — the same normalisation the server's search uses. */
export function normalizeMentionText(text: string | null | undefined): string {
  return (text ?? '').toLowerCase().replace(/ё/g, 'е')
}

export function mentionMatches(user: GroupUser, query: string): boolean {
  const q = normalizeMentionText(query)
  if (!q) return true
  return normalizeMentionText(user.username).includes(q) || normalizeMentionText(user.display_name).includes(q)
}

/** How well a loaded member matches — the same tiers as the server's ranking, lower is better. */
function matchRank(user: GroupUser, query: string): number {
  const q = normalizeMentionText(query)
  const username = normalizeMentionText(user.username)
  const display = normalizeMentionText(user.display_name)
  if (username === q || display === q) return 0
  if (username.startsWith(q)) return 1
  if (display.startsWith(q)) return 2
  if (display.split(/\s+/).some((word) => word.startsWith(q))) return 3
  return 4
}

export interface MentionSuggestionSources {
  query: string
  currentUserId: string | null
  lastMentioned: GroupUser | null
  /** Most recently active first. */
  recentlyActive: GroupUser[]
  /** The members loaded on this client, in list order. */
  members: GroupUser[]
  /** The server's best matches for `query` (empty while it's loading, or without a query). */
  remote: GroupUser[]
  /** Synthetic «@everyone» / «@here» / role entries, when allowed — placed after the personal tiers. */
  broadcast?: GroupUser[]
  limit?: number
}

export function rankMentionSuggestions(sources: MentionSuggestionSources): GroupUser[] {
  const { query, currentUserId, lastMentioned, recentlyActive, members, remote } = sources
  const limit = sources.limit ?? MENTION_SUGGESTIONS_LIMIT
  const result: GroupUser[] = []
  const seen = new Set<string>()
  const add = (user: GroupUser | null | undefined) => {
    if (!user || seen.has(user.id) || result.length >= limit || !mentionMatches(user, query)) return
    seen.add(user.id)
    result.push(user)
  }

  add(lastMentioned)

  let active = 0
  for (const user of recentlyActive) {
    if (active >= RECENT_ACTIVE_COUNT) break
    if (user.id === currentUserId || seen.has(user.id) || !mentionMatches(user, query)) continue
    add(user)
    active++
  }

  for (const entry of sources.broadcast ?? []) add(entry)

  for (const user of remote) add(user)

  const rest = query
      ? members.filter((u) => mentionMatches(u, query)).sort((a, b) => matchRank(a, query) - matchRank(b, query))
      : members
  for (const user of rest) add(user)

  return result
}
